import { useState } from "react";
import {
  saveReplacementValue,
  updateReplacementValue,
  deleteReplacementValue,
} from "../services/template-service";
import { useNotification } from "../../../context/notification-provider";
import type { ReplacementValueItem } from "../types/api-types";

interface UseReplacementValuesManagementProps {
  templateId: number;
  mutate: () => void;
}

export const useReplacementValuesManagement = ({
  templateId,
  mutate,
}: UseReplacementValuesManagementProps) => {
  const { showNotification } = useNotification();

  // Add/Edit state
  const [openDialog, setOpenDialog] = useState(false);
  const [formValues, setFormValues] = useState({
    value: "",
    newValue: "",
    comparisonTypeId: 0 as number,
  });
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editId, setEditId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [valueToDelete, setValueToDelete] = useState<{
    id: number;
    value: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setFormValues({ value: "", newValue: "", comparisonTypeId: 0 });
    setEditIndex(null);
    setEditId(0);
  };

  const handleOpenEditDialog = (index: number, item: ReplacementValueItem) => {
    setOpenDialog(true);
    setFormValues({
      value: item.value,
      newValue: item.newValue,
      comparisonTypeId: item.comparisonTypeId,
    });
    setEditIndex(index);
    setEditId(item.id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormValues({ value: "", newValue: "", comparisonTypeId: 0 });
    setEditIndex(null);
    setEditId(0);
  };

  const handleSave = async () => {
    if (!formValues.value.trim() || !formValues.newValue.trim()) return;

    setIsSaving(true);
    try {
      if (editIndex === null) {
        // Add new value
        await saveReplacementValue({
          id: 0,
          templateId,
          value: formValues.value.trim(),
          newValue: formValues.newValue.trim(),
          comparisonTypeId: formValues.comparisonTypeId,
        });
        showNotification({
          message: "Replacement value added successfully",
          severity: "success",
        });
      } else {
        // Update existing value
        await updateReplacementValue({
          id: editId,
          templateId,
          value: formValues.value.trim(),
          newValue: formValues.newValue.trim(),
          comparisonTypeId: formValues.comparisonTypeId,
        });
        showNotification({
          message: "Replacement value updated successfully",
          severity: "success",
        });
      }
      mutate();
      handleCloseDialog();
    } catch (err) {
      showNotification({
        message: `Failed to save replacement value. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: number, value: string) => {
    setValueToDelete({ id, value });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!valueToDelete) return;

    setIsDeleting(true);
    try {
      await deleteReplacementValue(valueToDelete.id);
      mutate();
      showNotification({
        message: "Replacement value deleted successfully",
        severity: "success",
      });
      setDeleteConfirmOpen(false);
      setValueToDelete(null);
    } catch (err) {
      showNotification({
        message: `Failed to delete replacement value. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setValueToDelete(null);
  };

  return {
    // Add/Edit dialog state
    openDialog,
    formValues,
    editIndex,
    isSaving,
    setFormValues,
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleSave,

    // Delete dialog state
    deleteConfirmOpen,
    valueToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useReplacementValuesManagement;
